import { useRouter } from 'next/router';
import { useSetRecoilState, useRecoilValue } from 'recoil';

import useMutationHook from '@/hooks/useMutationHook';
import useModal from '@/hooks/useModal';
import EasyLogin from '@/containers/modal/common/EasyLogin';
import { isLoginState } from '@/recoil';
import { plannerMuteState } from '@/recoil/chat';
import { API, URLs } from '@/api';
import { sendbird } from '@/utils/Sendbird';
import { FontStyle } from '@/utils/StyleUtil';

interface IPlanner {
    id?: number;
    name?: string;
    age?: number;
    sex?: number;
}

interface IProps {
    plannerData: IPlanner;
    insuranceType?: number;
    designRequestId?: number;
    proposalId?: number;
}

export default function useStartChatting({ plannerData, designRequestId, proposalId }: IProps) {
    const router = useRouter();
    const setIsMuted = useSetRecoilState(plannerMuteState);
    const isLogin = useRecoilValue(isLoginState);

    const { addModal, removeCurrentModal } = useModal();

    const openChattingPopup = async (mutate: Function) => {
        if (isLogin) {
            if (await sendbird.isBlockedPlanner(String(plannerData.id))) {
                addModal({
                    key: 'popUp',
                    props: {
                        type: 'noYes',
                        content: '차단한 플래너입니다.\n차단을 해제하고 상담을 시작하시겠습니까?',
                        contentStyle: FontStyle(14, 22, '400', '#444444', -1),
                        onClickYes: async () => {
                            await sendbird.unblockUser(String(plannerData.id));
                            setIsMuted(false);
                            mutate({});
                        },
                        onClickNo: removeCurrentModal,
                    },
                });
            } else {
                addModal({
                    key: 'popUp',
                    props: {
                        type: 'noYes',
                        content: '플래너와 채팅 상담을 시작할까요?',
                        contentStyle: FontStyle(14, 22, '400', '#444444', -1),
                        yesText: '예',
                        onClickYes: mutate,
                        noText: '아니오',
                        onClickNo: removeCurrentModal,
                    },
                });
            }
        } else {
            addModal({
                key: 'slide',
                props: {
                    content: <EasyLogin removeCnt={1} type="consult" />,
                    handleCloseModal: removeCurrentModal,
                },
            });
        }
    };

    const { mutate: createInsuranceChatting, errorPopUp } = useMutationHook(
        ['create-chat'],
        async () => {
            const res = await API.post(URLs.chat.initUsingProposalId(proposalId!));
            return res;
        },
        {
            onSuccess: chatRoomData => {
                router.push('/chat').then(() => {
                    router.push(
                        {
                            pathname: '/chat/room',
                            query: {
                                url: chatRoomData.channelUrl,
                                plannerId: plannerData.id,
                                plannerName: `${plannerData.name}`,
                                plannerSex: plannerData.sex,
                                plannerAge: plannerData.age,
                                designRequestId,
                            },
                        },
                        '/chat/room',
                    );
                });
            },
            onError: err => errorPopUp(err.response?.status),
        },
    );

    const { mutate: createChatting } = useMutationHook(
        ['create-chatting'],
        async () => {
            const res = await API.post(URLs.chat.initUsingPlannerId(plannerData.id!));
            return res;
        },
        {
            onSuccess: chatRoomData => {
                router.push('/chat').then(() => {
                    router.push(
                        {
                            pathname: '/chat/room',
                            query: {
                                url: chatRoomData.channelUrl,
                                plannerId: plannerData.id,
                                plannerName: `${plannerData.name}`,
                                plannerSex: plannerData.sex,
                                plannerAge: plannerData.age,
                            },
                        },
                        '/chat/room',
                    );
                });
            },
        },
    );

    const startChatting = async () => {
        openChattingPopup(createChatting);
    };

    const startInsuranceChatting = async () => {
        openChattingPopup(createInsuranceChatting);
    };

    return { startChatting, startInsuranceChatting };
}
